/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Switch,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Divider,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { useEventData } from "../EventDataContext";
import RedeemIcon from '@mui/icons-material/Redeem';
import SendIcon from '@mui/icons-material/Send';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      border: "1px solid #E0E0E0",
    },
    "&:hover fieldset": {
      border: "1px solid #BDBDBD",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #8A5918",
      boxShadow: "0 0 0 3px rgba(138, 89, 24, 0.1)",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
    },
  },
}));

const SectionTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
    <Box sx={{ width: '4px', height: '24px', backgroundColor: '#8A5918', mr: 1, borderRadius: '2px' }} />
    <Typography
      variant="h6"
      sx={{
        color: "#1A1A1A",
        fontWeight: 700,
        fontSize: "18px",
        textAlign: "left",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Label = ({ children, required }) => (
  <Typography
    sx={{
      color: "#4A4A4A",
      fontWeight: 600,
      fontSize: "14px",
      mb: 1,
      textAlign: "left",
    }}
  >
    {children} {required && <span style={{ color: "#E94C4C" }}>*</span>}
  </Typography>
);

export default function WishSettingsPage() {
  const { eventData, updateEventData, validationErrors } = useEventData();
  const wishData = eventData.wish;

  const hasError = (fieldName) => {
    return validationErrors?.wish?.[fieldName];
  };

  const getErrorMessage = (fieldName) => {
    return validationErrors?.wish?.[fieldName] || '';
  };

  const hasNestedError = (section, fieldName) => {
    return validationErrors?.wish?.[section]?.[fieldName];
  };

  const getNestedErrorMessage = (section, fieldName) => {
    return validationErrors?.wish?.[section]?.[fieldName] || '';
  };

  const iconOptions = [
    { value: "star", icon: <StarIcon />, label: "Star" },
    { value: "heart", icon: <FavoriteIcon />, label: "Heart" },
    { value: "thumbup", icon: <ThumbUpIcon />, label: "Thumb Up" },
    { value: "redeem", icon: <RedeemIcon />, label: "Redeem" },
    { value: "send", icon: <SendIcon />, label: "Send" },
  ];

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Sender Name Field</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={wishData.senderNameField?.enabled?.value || false}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  senderNameField: {
                    ...wishData.senderNameField,
                    enabled: { value: e.target.checked, required: false },
                  },
                })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={wishData.senderNameField?.enabled?.value ? "Enabled" : "Disabled"}
        />
      </Box>

      {wishData.senderNameField?.enabled?.value && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Label required={wishData.senderNameField?.enabled?.value}>Placeholder</Label>
            <StyledTextField
              placeholder="e.g. Your Name"
              value={wishData.senderNameField?.placeholder?.value || ""}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  senderNameField: {
                    ...wishData.senderNameField,
                    placeholder: { value: e.target.value, required: true },
                  },
                })
              }
              error={hasNestedError('senderNameField', 'placeholder')}
              helperText={getNestedErrorMessage('senderNameField', 'placeholder')}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Label required>Character Limit</Label>
            <StyledTextField
              placeholder="e.g. 50"
              type="number"
              value={wishData.senderNameField?.characterLimit?.value || ""}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  senderNameField: {
                    ...wishData.senderNameField,
                    characterLimit: {
                      value: e.target.value,
                      required: true,
                    },
                  },
                })
              }
              error={hasNestedError('senderNameField', 'characterLimit')}
              helperText={getNestedErrorMessage('senderNameField', 'characterLimit')}
            />
          </Grid>
        </Grid>
      )}

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Wish Field</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={wishData.wishField?.enabled?.value || false}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  wishField: {
                    ...wishData.wishField,
                    enabled: { value: e.target.checked, required: false },
                  },
                })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={wishData.wishField?.enabled?.value ? "Enabled" : "Disabled"}
        />
      </Box>

      {wishData.wishField?.enabled?.value && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Label required={wishData.wishField?.enabled?.value}>Placeholder</Label>
            <StyledTextField
              placeholder="e.g. Best wishes!"
              value={wishData.wishField?.placeholder?.value || ""}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  wishField: {
                    ...wishData.wishField,
                    placeholder: { value: e.target.value, required: true },
                  },
                })
              }
              error={hasNestedError('wishField', 'placeholder')}
              helperText={getNestedErrorMessage('wishField', 'placeholder')}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Label required>Character Limit</Label>
            <StyledTextField
              placeholder="e.g. 200"
              type="number"
              value={wishData.wishField?.characterLimit?.value || ""}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  wishField: {
                    ...wishData.wishField,
                    characterLimit: {
                      value: e.target.value,
                      required: true,
                    },
                  },
                })
              }
              error={hasNestedError('wishField', 'characterLimit')}
              helperText={getNestedErrorMessage('wishField', 'characterLimit')}
            />
          </Grid>
        </Grid>
      )}

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Confirm Button</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={wishData.confirmButton?.enabled?.value || false}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  confirmButton: {
                    ...wishData.confirmButton,
                    enabled: { value: e.target.checked, required: false },
                  },
                })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={wishData.confirmButton?.enabled?.value ? "Enabled" : "Disabled"}
        />
      </Box>

      {wishData.confirmButton?.enabled?.value && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Label>Button Icon</Label>
            <FormControl fullWidth>
              <Select
                value={wishData.confirmButton?.icon?.value || "star"}
                onChange={(e) =>
                  updateEventData("wish", {
                    ...wishData,
                    confirmButton: {
                      ...wishData.confirmButton,
                      icon: { value: e.target.value, required: false },
                    },
                  })
                }
                IconComponent={ExpandMoreIcon}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8A5918",
                  },
                  "& .MuiSelect-select": {
                    padding: "12px 16px",
                  },
                }}
              >
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {option.icon}
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <Label required={wishData.confirmButton?.enabled?.value}>Button Text</Label>
            <StyledTextField
              placeholder="e.g. Send Wish"
              value={wishData.confirmButton?.text?.value || ""}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  confirmButton: {
                    ...wishData.confirmButton,
                    text: { value: e.target.value, required: true },
                  },
                })
              }
              error={hasNestedError('confirmButton', 'text')}
              helperText={getNestedErrorMessage('confirmButton', 'text')}
            />
          </Grid>
        </Grid>
      )}

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Random Wish</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={wishData.randomWish?.enabled?.value || false}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  randomWish: {
                    ...wishData.randomWish,
                    enabled: { value: e.target.checked, required: false },
                  },
                })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={wishData.randomWish?.enabled?.value ? "Enabled" : "Disabled"}
        />
      </Box>

      {wishData.randomWish?.enabled?.value && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                color: "#8A5918",
                borderColor: "#8A5918",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#6b3b07",
                  backgroundColor: "rgba(138, 89, 24, 0.04)",
                },
              }}
            >
              Add Random Wishes
            </Button>
          </Grid>
        </Grid>
      )}

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Preview Button</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={wishData.previewButton?.enabled?.value || false}
              onChange={(e) =>
                updateEventData("wish", {
                  ...wishData,
                  previewButton: {
                    ...wishData.previewButton,
                    enabled: { value: e.target.checked, required: false },
                  },
                })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={wishData.previewButton?.enabled?.value ? "Enabled" : "Disabled"}
        />
      </Box>

      {wishData.previewButton?.enabled?.value && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Label>Button Icon</Label>
            <FormControl fullWidth>
              <Select
                value={wishData.previewButton?.icon?.value || "star"}
                onChange={(e) =>
                  updateEventData("wish", {
                    ...wishData,
                    previewButton: {
                      ...wishData.previewButton,
                      icon: { value: e.target.value, required: false },
                    },
                  })
                }
                IconComponent={ExpandMoreIcon}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8A5918",
                  },
                  "& .MuiSelect-select": {
                    padding: "12px 16px",
                  },
                }}
              >
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {option.icon}
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
